import { useState } from "react"
import { Check, Sparkles, ExternalLink, Loader2 } from "lucide-react"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { PRODUCTS, getProductByPlan } from "@/lib/products"
import { useAuth } from "@/lib/auth-context"
import { createCheckoutSession } from "@/lib/api-client"
import { useToast } from "@/components/ui/use-toast"

export default function BillingPage() {
  const { user } = useAuth()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState<string | null>(null)

  const currentPlan = getProductByPlan(user?.plan || "free")

  const handleSelectPlan = async (priceId?: string) => {
    if (!priceId) return // Free plan — no checkout needed
    setIsLoading(priceId)

    try {
      const { url } = await createCheckoutSession(priceId)
      window.location.href = url
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout.",
        variant: "destructive",
      })
      setIsLoading(null)
    }
  }

  return (
    <div className="flex-1 p-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">
            Plans & Billing
          </h1>
          <p className="mt-1 text-muted-foreground">
            Choose the right plan for your investor relations needs
          </p>
        </div>

        {/* Current plan banner */}
        <Card className="mb-8 border-primary/20 bg-primary/5">
          <CardContent className="flex items-center justify-between p-6">
            <div>
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <p className="text-lg font-semibold text-foreground">
                {currentPlan?.name || "Free"}
              </p>
              {user?.plan && user.plan !== "free" && (
                <p className="text-sm text-muted-foreground">
                  {currentPlan?.price === 0 ? "No charge" : `$${currentPlan?.price}/${currentPlan?.interval}`}
                </p>
              )}
            </div>
            <Badge variant="secondary" className="bg-primary/10 text-primary">
              {user?.plan === "free" ? "Free Plan" : "Active"}
            </Badge>
          </CardContent>
        </Card>

        {/* Pricing cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map((product) => {
            const isCurrent = currentPlan?.id === product.id
            return (
              <Card
                key={product.id}
                className={cn(
                  "relative flex flex-col transition-shadow hover:shadow-lg",
                  product.highlighted && "border-primary shadow-md",
                  isCurrent && "ring-2 ring-primary"
                )}
              >
                {product.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground gap-1">
                      <Sparkles className="h-3 w-3" />
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">{product.name}</CardTitle>
                    {isCurrent && (
                      <Badge variant="outline" className="text-xs">Current</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground min-h-[2.5rem]">
                    {product.description}
                  </p>
                  <div className="pt-2">
                    <span className="text-3xl font-bold text-foreground">
                      {product.price === 0 ? "Free" : `$${product.price}`}
                    </span>
                    {product.price > 0 && (
                      <span className="text-muted-foreground">/{product.interval}</span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-1">
                  <ul className="flex flex-col gap-3">
                    {product.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  {isCurrent ? (
                    <Button disabled className="w-full" variant="outline">
                      Current Plan
                    </Button>
                  ) : product.priceId ? (
                    <Button
                      onClick={() => handleSelectPlan(product.priceId)}
                      disabled={isLoading !== null}
                      className={cn(
                        "w-full",
                        product.highlighted
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : ""
                      )}
                      variant={product.highlighted ? "default" : "outline"}
                    >
                      {isLoading === product.priceId ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Upgrade"
                      )}
                    </Button>
                  ) : (
                    <Button disabled className="w-full" variant="outline">
                      Free Forever
                    </Button>
                  )}
                </CardFooter>
              </Card>
            )
          })}
        </div>

        {/* FAQ section */}
        <div className="mt-12">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Can I switch plans later?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Is there a free trial?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  All paid plans include a 14-day free trial. No credit card required to start on the free plan.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">What happens if I cancel?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Your data is retained for 30 days after cancellation. You can export everything at any time. You'll be downgraded to the free plan.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Do you offer annual billing?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Annual billing is coming soon with a 20% discount. Contact us for early access.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
